import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React, { useState } from "react";
import "../../../css/Common/Pages/AboutUsPage/AboutUsPage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import { ReactComponent as RosemountInMediaIcon } from "../../../assets/icons/Rosemount in Media.svg";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import { useNavigate } from "react-router-dom";
import MeetTheTeamCard from "frontend/Components/DesignSystem/Cards/MeetTheTeamCard";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, SEO_SITE, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * AdviserAboutUsPage component renders the "About Us" page for the Adviser section.
 *
 * This page provides an overview of the Rosemount adviser network, including its mission,
 * opportunities for advisers, and a showcase of the team members. It features navigation,
 * a hero section, team member profiles, service cards, and contact/footer components.
 *
 * Features:
 * - Navigation bar with relevant links for advisers.
 * - Hero section with a title and subtitle.
 * - Informational sections about the adviser network and its benefits.
 * - "Meet the Team" section displaying team member profiles.
 * - Service cards for media, testimonials (with modal popup), and events.
 * - Call-to-action to join the network.
 * - Contact and footer sections.
 *
 * State:
 * - `isModalOpen` (boolean): Controls the visibility of the "Hear from our Advisers" video modal.
 *
 * @component
 * @returns {JSX.Element} The rendered About Us page for advisers.
 */
const AdviserAboutUsPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Toggles the state of the modal between open and closed.
   * When called, it inverts the current value of `isModalOpen`.
   *
   * @function
   * @returns {void}
   */
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const CEO = [
    {
      imgSrc: ImageDirectory["ahmed"],
      title: "",
      name: "Ahmed Bawa",
      role: "CEO",
      altText:
        "A man smiling in a dark suit and polka dot of dark blue and red dots tie stood in front of a dark blue background",
      bio: "Ahmed started Rosemount in 2006 after working in financial services for the last 30 years. As CEO he is passionate about his advisers and their clients. Ahmed completed his MBA from Cardiff Business School. Away from work he enjoys running, skiing, scuba diving and watching his beloved Arsenal play at the Emirates.",
    },
  ];

  const Team = [
    {
      imgSrc: ImageDirectory["ahmed"],
      title: "",
      name: "Ahmed Bawa",
      role: "CEO",
      altText:
        "A man smiling in a dark suit and polka dot of dark blue and red dots tie stood in front of a dark blue background",
      bio: "Ahmed started Rosemount in 2006 after working in financial services for the last 30 years. As CEO he is passionate about his advisers and their clients. Ahmed completed his MBA from Cardiff Business School. Away from work he enjoys running, skiing, scuba diving and watching his beloved Arsenal play at the Emirates.",
    },
    {
      imgSrc: ImageDirectory["zac"],
      name: "Zachary Bawa",
      role: "Sales Director",
      altText:
        "A man smiling man with dark hair and a moustache in a navy blue suit and red tie stood in front of a dark blue background",
      bio: "Zac joined Rosemount full-time in September 2022 after having completed a Microbiology degree at Imperial College London. Zac is interested in modernising and automating processes. In his free time, he enjoys fishing and is an Arsenal fan.",
    },
    {
      imgSrc: ImageDirectory["anne"],
      name: "Anne Sykes",
      role: "Strategic Adviser Manager",
      altText:
        "A smiling woman in a black top with red dots on stood in front of a dark blue background",
      bio: "Anne’s favourite part of working at Rosemount is our team and how each day brings a new and different challenge. She enjoys spending time with her two English Bull Terriers and taking them out for walks. An interesting fact about Anne is she is Rosemount’s longest serving team member and loves everything vintage.",
    },
    {
      imgSrc: ImageDirectory["Iain"],
      name: "Iain Crowley",
      role: "Head of Compliance",
      altText:
        "A smiling man in a dark suit with a navy blue tie with light blue stripes stood in front of a dark blue background",
      bio: "Iain enjoys getting involved in different areas of the business which makes time fly. His hobbies include walking, travelling and an interest in investing & stock markets. He likes planes and his daughter is an engineer at Airbus.",
    },
    {
      imgSrc: ImageDirectory["paul"],
      name: "Paul Strongitharm",
      role: "Head of Accounts",
      altText:
        "A man in a dark suit with a purple tie stood in front of a dark blue background",
      bio: "Paul has said it is impossible to narrow it down to just one thing which he enjoys at Rosemount. In his spare time he likes to buy and sell Subbuteo and football programmes.",
    },
    {
      imgSrc: ImageDirectory["callum"],
      name: "Callum Wilkinson",
      role: "Strategic Relationship Manager",
      altText:
        "A smiling man with a dark suit on and a yellow tie stood in front of a dark blue background",
      bio: "Callum enjoys that every day is varied and getting to speak to the advisers and partners daily. His hobbies include football - being a Crystal Palace fan, golfing, paddleball, and tennis. He also enjoys quizzes and logic puzzles. Before joining Rosemount Callum worked as a security guard at Debenhams.",
    },
    {
      imgSrc: ImageDirectory["jacob"],
      name: "Jacob Bawa",
      role: "Operations Director",
      altText:
        "A smiling man with a cheque navy suit on with a light blue tie stood in front of a dark blue background",
      bio: "Jacob said his favourite part of working for Rosemount is the diversity and variation in his role. During his free time, he likes going to the gym, fishing, and darts. Also, anything car related. Something interesting about Jacob is that he is a qualified PADI Advanced Open Water Scuba Diver completing over 50 dives.",
    },

    // {
    //   imgSrc: ImageDirectory["Hayleigh"],
    //   name: "Hayleigh Kelly",
    //   role: "Deputy Money Laundering Reporting Officer",
    //   altText:
    //     "A smiling woman with a white top on stood in front of a dark blue background",
    //   bio: "Hayleigh enjoys the chance to work on new and exciting projects in her role. She enjoys reading and getting to spend her free time with her family. An interesting fact about her is that she is older than Google.",
    // },
    {
      imgSrc: ImageDirectory["Ranil"],
      title: "The Rt Hon.",
      name: "Sir Ranil Jayawardena",
      role: "Non-Executive Adviser",
      altText: "",
      bio: "Alongside banking experience, Ranil served in government for 15+ years, including as Trade Minister and Environment Secretary. Having completed a degree from the London School of Economics, and a year at the Royal College of Defence Studies, Ranil follows geopolitics closely. He enjoys watching cricket at Lord’s, where he’s a member.",
    },
    {
      imgSrc: ImageDirectory["dallas"],
      name: "Dallas Manasco",
      role: "HR & Operations Manager",
      altText:
        "A smiling woman, with her dark hair back and glasses on in a black shirt stood in front of a white background",
      bio: "Dallas enjoys the friendly and supportive atmosphere of the family run business. As well as working with people who care about their clients and each other. Dallas’ hobbies include reading, photography and getting to knit wonky sweaters. An interesting fact is she grew up in New York, had a stint living in Gibraltar and now settled in Hampshire.",
    },
    {
      imgSrc: ImageDirectory["grace"],
      name: "Grace Furner",
      role: "Mortgage & Protection Compliance Officer",
      altText:
        "A smiling woman with a jacket and black top, with silver necklace stood in front of a light blue background",
      bio: "Grace said the best part of Rosemount is working with an amazing team and the fact that no two days are the same. In her spare time, she enjoys hiking, skiing, occasionally going to the gym, and watching true crime documentaries. She once visited eight countries in just two weeks.",
    },
    {
      imgSrc: ImageDirectory["Neshitha"],
      name: "Neshitha Pathmanathan",
      role: "Mortgage & Protection Compliance Officer",
      altText: "",
      bio: "Neshitha enjoys having the opportunity to work on a variety of different projects within the business at Rosemount. In her free time, she likes playing badminton and going to the gym. An interesting fact about Neshitha is that she successfully completed eight grades of the LAMDA qualification.",
    },
    {
      imgSrc: ImageDirectory["eshaan"],
      name: "Eshaan Singh",
      role: "Mortgage & Protection Compliance Officer",
      altText: "",
      bio: "Eshaan enjoys the welcoming and supportive environment at Rosemount, where the friendly team creates a homely atmosphere that makes him feel valued and motivated. Outside of work, he enjoys playing football, running, developing AI agents, and DJing. An interesting fact about Eshaan is that he is a passionate Pokémon card collector, with a collection that has grown in value over the years. ",
    },
    {
      imgSrc: ImageDirectory["siddharth"],
      name: "Siddharth Nakul",
      role: "Mortgage & Protection Compliance Officer",
      altText: "",
      bio: "Sidd enjoys the positive culture and supportive team atmosphere at Rosemount. He finds it rewarding to work with people who genuinely help one another while developing his skills. Outside of work, he enjoys cooking, trying new restaurants, travelling, playing badminton, and hosting events. He also ran his own baking business for three years before relocating for university. ",
    },
    {
      imgSrc: ImageDirectory["jess"],
      name: "Jess Bass",
      role: "Financial Planning Compliance Officer",
      altText:
        "A smiling woman with dark hair and a jacket with a black top stood in front of a light blue background",
      bio: "Jess likes being part of a community within the Rosemount team and working alongside her colleagues. She enjoys reading, walking and spending time with her family and friends. She is currently learning how to solve a Rubik’s cube.",
    },
    {
      imgSrc: ImageDirectory["Faizaan"],
      name: "Faizaan Abbas",
      role: "Financial Planning Compliance Officer",
      altText: "",
      // bio: "",
       bio: "Faizaan’s favourite part about working at Rosemount is the friendly and supportive environment, which has helped him learn and develop in his role. In his free time, he enjoys fitness and sports, particularly cricket, having played at a high-level in Pakistan and being a Level 2 qualified coach. An interesting fact about Faizaan is that he is a second-grade black belt in karate.",
    },

    {
      imgSrc: ImageDirectory["aled"],
      name: "Aled Rees",
      role: "Financial Planning Compliance Officer",
      altText: "",
      bio: "Aled enjoys the friendly, welcoming atmosphere, and the air conditioning is a bonus in the summer! Outside of work, he enjoys running, from sprint races for his university track team to marathons, and unwinding with games like Call of Duty and Super Smash Bros. One of his favourite memories is when he swam with dolphins in Mexico. ",
    },

    {
      imgSrc: ImageDirectory["luke"],
      name: "Luke Onuoha",
      role: "Financial Planning Compliance Officer",
      altText: "",
      bio: "Luke enjoys the supportive team environment at Rosemount and has enjoyed learning about a new area of financial services beyond his degree. Outside of work, he likes going to the gym and training in MMA. An interesting fact about Luke is that he has played at Saracens Stadium.",
    },
    {
      imgSrc: ImageDirectory["hamza"],
      name: "Hamza Nazir",
      role: "Financial Planning Compliance Officer",
      altText: "",
      bio: "Hamza enjoys learning about different financial services products and values the friendly, welcoming atmosphere within the compliance team. Outside of work, he enjoys football, motorsports, snooker, basketball, and films. Hamza also teaches children go-karting on the weekends, and one of his students won the British Indoor Karting Championship last year.",
    },
    {
      imgSrc: ImageDirectory["chloe"],
      name: "Chloe Denovan",
      role: "Mortgage & Protection Paraplanner",
      altText:
        "A smiling woman with slicked back hair in a ponytail with a white cardigan and white top on with a gold necklace and earrings stood in front of a light blue background",
      bio: "Chloe likes the colleagues, the staff environment and the fact that she does not feel under pressure in her role. She enjoys singing, drawing and baking in her spare time. She has completed her 4 Performing Arts Singing exams at the University of West London and achieved the highest grade, distinction, in all of them.",
    },
    // {
    //   imgSrc: ImageDirectory["Pam"],
    //   name: "Anupam Limbu",
    //   role: "Mortgage & Protection Paraplanner",
    //   altText: "An outline of a woman icon",
    //   bio: "Anupam enjoys the friendly and welcoming Rosemount team. During her free time, she likes to watch different types of movies as well as designing and stitching her own clothing creations. As well as this Anupam has graduated in Fashion Design.",
    // },
    {
      imgSrc: ImageDirectory["jacko"],
      name: "Jacko Lam",
      role: "Financial Planning Paraplanner",
      altText:
        "A smiling man with black hair and a blue shirt with a black tie on stood in front of a light blue background",
      bio: "Jacko enjoys everything about his role at Rosemount. In his free time, he likes to watch football and Netflix as well as the occasional cycling trip. He enjoys investing in stocks and shares.",
    },
    {
      imgSrc: ImageDirectory["davin"],
      name: "Davin Thomas",
      role: "Financial Planning Paraplanner",
      altText: "",
      bio: "Davin enjoys working at Rosemount because of its fast-paced environment, the people he works with, and the variety of cases he handles each day. Outside of work, he enjoys playing FIFA, especially taking teams from a relegation battle to success. In the future, he hopes to start his own business, so he’s focused on building his network and developing new skills.",
    },
    // {
    //   imgSrc: ImageDirectory["Elliot"],
    //   name: "Elliot Burley",
    //   role: "Financial Planning Paraplanner",
    //   altText: "",
    //   bio: "Elliot said the best part of working at Rosemount is the people. They’re very knowledgeable, and he has the chance to learn so much from them. Elliot’s hobbies include anything car‑related, shooting, skiing, and going to the gym. An interesting fact about him is he was captain of one of the best junior clay pigeon shooting teams in England.",
    // },
    {
      imgSrc: ImageDirectory["Kavita"],
      name: "Kavita Dedhia",
      role: "UX/UI Designer",
      altText: "",
      bio: "Kavita enjoys the fast-paced environment at Rosemount and values its supportive, collaborative culture. She is an avid tennis enthusiast who also enjoys running, hiking, and traveling, often capturing her trips through gouache paintings. Notably, she completed a 14,000-ft Himalayan trek at just 11 years old.",
    },
    {
      imgSrc: ImageDirectory["Zack"],
      name: "Zack Gill",
      role: "Marketing & Social Media",
      altText: "",
      bio: "Zack’s favourite part about working at Rosemount is the friendly, fast-paced environment. In Zack’s free time, he enjoys playing video games and watching his favourite football team, QPR. An interesting fact about Zack is that he once played in a tournament representing his favourite team at Wembley Stadium.",
    },
    {
      imgSrc: ImageDirectory["Rose"],
      name: "Rose Morfydd",
      role: "Graphic & Web Designer",
      altText: "",
      bio: "Rose’s favourite part of Rosemount is that she enjoys working with the advisers to update and create modern brand designs. In Rose’s free time she enjoys playing the guitar and drums, mountain boarding and fine line pen drawing. An interesting fact is that Rose was featured in the Basingstoke Gazette for her graphic design work.",
    },
    {
      imgSrc: ImageDirectory["Lauren"],
      name: "Lauren Smit",
      role: "Graphic Designer",
      altText: "Lauren Smit",
      bio: "Lauren’s favourite part about working at Rosemount is the variety in her work as a graphic designer, enjoying the opportunity to work on different brands throughout the day. In her free time, she loves watercolour painting, air-dry clay modelling and trying out any new craft she can get her hands on. An interesting fact about Lauren is that she has a rescue dog from South Africa who is the light of her life.",
    },

    // {
    //   imgSrc: ImageDirectory["timo"],
    //   name: "Timo Griffiths",
    //   role: "Marketing/Social Media",
    //   altText:
    //     "A smiling man with glasses and a black suit with a red tie stood in front of a light blue background",
    //   bio: "Timo’s favourite part of Rosemount is that he enjoys having a wide range of creative briefs and the day-to-day variation. He enjoys the outdoors and swimming when he gets the chance. An interesting fact about Timo is that he was born with green hair due to copper in the water pipes in the 70s!",
    // },

    // {
    //   imgSrc: ImageDirectory["hrutuja"],
    //   name: "Hrutuja Patnekar",
    //   role: "Full Stack Developer",
    //   altText:
    //     "A smiling woman with her hair down wearing a black shirt with white dots on stood in front of a light blue background",
    //   bio: "Hrutuja thinks the best thing about working at Rosemount is the warm and welcoming environment, it really feels like being part of a family. Outside of work, she is obsessed with her garden. She is incredibly passionate about her Master’s in Artificial Intelligence from University of St Andrews and geeking out over all things tech.",
    // },
    // {
    //   imgSrc: ImageDirectory["shashank"],
    //   name: "Shashank Bedade",
    //   role: "UI/UX Designer",
    //   altText: "",
    //   bio: "Shashank’s favourite part of Rosemount is the fast-paced, real-world energy. One of his hobbies is singing, you may see him perform in London on the weekends. He also enjoys graphite pencil portraits. An interesting fact about Shashank is he was singing in the street which drew a crowd. It reminded him of authenticity and connection through art.",
    // },
    // {
    //   imgSrc: ImageDirectory["leilah"],
    //   name: "Leilah Sadat",
    //   role: "Adviser Support",
    //   altText:
    //     "A smiling woman with curly dark hair in a black and white striped jumper stood in front of a light blue background",
    //   bio: "Leilah enjoys the friendly nature of the network compared to others, as well as the fact that it is family owned. She enjoys knitting in her spare time and has made several blankets. She also has a degree in Journalism from Portsmouth University.",
    // },

    {
      imgSrc: ImageDirectory["Angel"],
      name: "Angel Strongitharm",
      role: "Adviser Support",
      altText: "",
      bio: "Angel said the best part about working at Rosemount is the friendly environment and how supportive everyone is. In her free time, she enjoys crocheting, listening to music, and going to the gym. An interesting fact about Angel is she once won an air fryer at bingo.",
    },
    {
      imgSrc: ImageDirectory["André"],
      name: "André O'Leary",
      role: "Business Administrator",
      altText: "",
      bio: "André really enjoys the fact that no day is ever the same at Rosemount. He likes getting to speak with advisers daily and the trust that is placed upon him from the senior leadership team. Outside of work, André is actively involved serving in his church in Reading and is an instrumentalist there!",
    },
    {
      imgSrc: ImageDirectory["Stephen"],
      name: "Stephen Chan",
      role: "Finance & Accounts",
      altText:
        "A smiling man with glasses in a black suit, light blue shirt and black tie stood in front of a light blue background",
      bio: "Stephen enjoys working in a team environment and has learned a lot by collaborating with other members of staff. During his free time, he enjoys jazz music as well as reading mystery novels. Something interesting about Stephen is that he loves watching UFO and alien related news and videos, one day he hopes to meet an alien!",
    },
    {
      imgSrc: ImageDirectory["bethany"],
      name: "Bethany Wyatt",
      role: "Finance & Accounts",
      altText: "",
      bio: "Bethany enjoys the learning opportunities at Rosemount and values the chance to continually develop her knowledge and skills in a supportive environment. Outside of work, she enjoys collecting military antiques, cooking, and exploring new cuisines. A fun fact about Bethany is that she owns a rare CT12-IM gas mask.",
    },
  ];
  const teamStructuredData = {
    "@type": "ItemList",
    name: "Rosemount Financial Solutions Team",
    description:
      "Meet the Rosemount Financial Solutions head office team supporting advisers across the United Kingdom.",
    itemListElement: Team.map((teamMember, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Person",
        name: teamMember.title
          ? `${teamMember.title} ${teamMember.name}`
          : teamMember.name,
        jobTitle: teamMember.role,
        worksFor: {
          "@id": `${SEO_SITE.siteUrl}/#organization`,
        },
        description: teamMember.bio,
      },
    })),
  };
  const navigate = useNavigate();
  return (
    <div className="AboutUs-Page">
      <SeoHelmet
        page={SEO_PAGES.aboutUs}
        service={{
          name: "Adviser Network Support",
          description:
            "Rosemount Financial Solutions supports mortgage advisers, protection specialists and financial planners through compliance support, business development, training, adviser events and head office support.",
          serviceType: [
            "Mortgage Adviser Support",
            "Protection Adviser Support",
            "Financial Planning Adviser Support",
            "Compliance Support",
            "Business Development Support",
            "Adviser Training",
            "Head Office Support",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "About Us",
            url: getPageUrl("/Adviser/AboutUs"),
          },
        ]}
        extraSchemas={[teamStructuredData]}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["about-us"]}
        isAdviser={true}
        altText="Two advisers in business attire smiling next to a laptop."
        children={
          <>
            <h1>About Us</h1>
            <div className="SubTitle">
              Join our diverse adviser network to connect, collaborate, and
              thrive in a supportive community
            </div>
          </>
        }
      />
      <div className="AboutUs-Container">
        <div className="SubTitle">
          Our adviser network is a community of skilled advisers from diverse
          backgrounds, all dedicated to making a positive impact in the lives of
          others. Our goal is to connect advisers and help them build meaningful
          relationships, share knowledge, and support each other’s growth and
          development.
        </div>
        <div className="SubTitle">
          Our network provides opportunities for advisers to connect,
          collaborate, and exchange ideas through various events, webinars, and
          a private online forum. Whether you’re an experienced adviser or just
          starting out, our network welcomes you and offers a supportive
          community to help you thrive in your role.
        </div>
        <div className="SubTitle">
          We know that working with a network is about more than compliance or
          FCA authorisation, you need to trust the team you are working with. At
          Rosemount, we have a strong team on hand to support our advisers each
          and every day. Meet our Head Office team members below, and read a
          little bit more about them, what they enjoy about Rosemount and what
          they get up to in their spare time.
        </div>
      </div>
      <div className="Meet-The-Team-Container">
        <h2>Meet the Team</h2>
        {/* <MeetTheTeamCard
          imgSrc={CEO[0].imgSrc}
          title={CEO[0].title}
          name={CEO[0].name}
          role={CEO[0].role}
          bio={CEO[0].bio}
          altText={CEO[0].altText}
        /> */}
        <div className="Meet-The-Team-Image-Container">
          {Team.map((teamMember) => (
            <MeetTheTeamCard
              key={`${teamMember.name}-${teamMember.role}`}
              imgSrc={teamMember.imgSrc}
              title={teamMember.title || ""}
              name={teamMember.name}
              role={teamMember.role}
              bio={teamMember.bio}
              altText={teamMember.altText}
            />
          ))}
        </div>
      </div>
      <div className="Services-Info-box1">
        <div className="Services-Info-box1-Content">
          <h3>
            Join the Rosemount Network for personalised support, expert
            guidance, and the tools you need to achieve your full potential.
          </h3>
          <ButtonYellow
            buttonText="Join Us"
            onClick={() => {
              navigate("/Adviser/JoinUs");
            }}
          />
        </div>
      </div>

      <div className="Adviser-Services-Container Adviser-Services-Holder">
        <CardProduct
          Icon={RosemountInMediaIcon}
          title="Rosemount in the Media"
          link="/RosemountInMedia"
          isAdviser={true}
        />
        <div onClick={handleToggleModal}>
          <CardProduct
            Icon={HearFromOurAdvisersIcon}
            title="Hear from our Advisers"
            isAdviser={true}
          />
        </div>
        <VideoCarouselPopup
          isOpen={isModalOpen}
          onClose={handleToggleModal}
          // videos={videos}
        />
        <CardProduct
          Icon={EventsIcon}
          title="Events"
          link="/Adviser/Events"
          isAdviser={true}
        />
      </div>
      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default AdviserAboutUsPage;
